import PageBreadcrumb, {
  PageBreadcrumbItem
} from 'components/common/PageBreadcrumb';
import SearchBox from 'components/common/SearchBox';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useState } from 'react';
import UserTable, { getUserTablecolumns } from './table/UserTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import InviteUserModal from 'components/modals/InviteUserModal';
import Button from 'components/base/Button';
import {
  useDeleteUserInviteMutation,
  useFindInvitedUserQuery,
  useResentUserInviteMutation,
  useUpdateUserInviteStatusMutation
} from 'service/authQuery/authQuery';
import {
  ConfirmModalMapping,
  FormModal,
  FormType,
  User,
  UsersResponse
} from './users.type';
import ConfirmationDialog from 'components/base/ConfirmationDialog';
import { useToast } from 'hooks/useToast';
import CanAccess from 'components/modules/auth/Access/CanAccess';

const defaultBreadcrumbItems: PageBreadcrumbItem[] = [
  {
    label: 'Users',
    url: '/hub/users',
    active: true
  }
];

const initialFormModal: FormModal = {
  flag: false,
  data: null,
  type: 'FORM'
};

function Users() {
  const [formModal, setFormModal] = useState<FormModal>(initialFormModal);
  const { data = [], isFetching } = useFindInvitedUserQuery('');
  const [updateUserStatus, { isLoading: updateStatusLoading }] =
    useUpdateUserInviteStatusMutation();
  const [deleteUserInvite, { isLoading: deleteInviteLoading }] =
    useDeleteUserInviteMutation();
  const [inviteUser, { isLoading: inviteUserLoading }] =
    useResentUserInviteMutation();

  const { open } = useToast();

  const handleEdit = (item: User) => {
    setFormModal({
      flag: true,
      data: item,
      type: 'FORM'
    });
  };

  const handleStatusChange = (item: User) => {
    setFormModal({
      flag: true,
      data: item,
      type: 'STATUS'
    });
  };

  const handleDeleteUserInvite = (item: User) => {
    setFormModal({
      flag: true,
      data: item,
      type: 'DELETE'
    });
  };

  const handleInvite = (item: User) => {
    setFormModal({
      flag: true,
      data: item,
      type: 'INVITE'
    });
  };

  const table = useAdvanceTable({
    data: data as UsersResponse,
    columns: getUserTablecolumns({
      onEdit: handleEdit,
      onAccountStatusChange: handleStatusChange,
      onDelete: handleDeleteUserInvite,
      onInvite: handleInvite
    }),
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false,
    isLoading: isFetching
  });

  const handleConfirm = () => {
    const payload = {
      id: formModal.data?._id
    };
    const handler =
      formModal.type === 'STATUS'
        ? updateUserStatus
        : formModal.type === 'DELETE'
        ? deleteUserInvite
        : inviteUser;
    handler(payload)
      .unwrap()
      .then(response => {
        open(response?.message, 'success');
        setFormModal(initialFormModal);
      })
      .catch(error => {
        console.log(error);
        const message = error?.data?.error || 'Something went wrong';
        open(message, 'error');
      });
  };

  const confirmModalMapping: Record<FormType, ConfirmModalMapping> = {
    FORM: {
      confirmButtonLabel: '',
      title: '',
      body: <></>
    },
    STATUS: {
      confirmButtonLabel: 'Proceed',
      title: formModal.data?.isActive
        ? 'Deactivate Account'
        : 'Activate Account',
      body: (
        <>
          <h5>
            {formModal.data?.isActive
              ? 'Do you really want to deactivate this account?'
              : 'Are you sure you wish to reactivate this account?'}
          </h5>
          <p className="text-body-tertiary text-opacity-85">
            {formModal.data?.isActive
              ? 'Deactivating this account will suspend access and all associated activities. You can reactivate it later if needed.'
              : 'Reactivating this account will restore access and enable all previous functionalities. Proceed with caution if this was not intended.'}
          </p>
        </>
      )
    },
    DELETE: {
      confirmButtonLabel: 'Delete',
      title: 'Confirm Account Deletion',
      body: (
        <>
          <h5>Are You Sure You Want to Delete?</h5>
          <p className="text-body-tertiary text-opacity-85">
            Once deleted, this account and all its data will be permanently
            erased. You won’t be able to recover it. Please confirm if you wish
            to continue.
          </p>
        </>
      )
    },
    INVITE: {
      confirmButtonLabel: 'Confirm',
      title: 'Confirm Sending Invitation',
      body: (
        <>
          <h5>Are you sure you want to send an invitation to this user?</h5>
          <p className="text-body-tertiary text-opacity-85">
            The invitation will be sent to {formModal.data?.email}, and they
            will receive instructions to join. Please confirm if you want to
            proceed.
          </p>
        </>
      )
    }
  };

  return (
    <div>
      <InviteUserModal
        show={formModal.flag && formModal.type === 'FORM'}
        onClose={() => setFormModal(initialFormModal)}
        data={formModal.data}
      />
      <ConfirmationDialog
        show={formModal.type !== 'FORM'}
        onConfirm={handleConfirm}
        onHide={() => setFormModal(initialFormModal)}
        title={confirmModalMapping[formModal.type].title}
        isLoading={
          updateStatusLoading || deleteInviteLoading || inviteUserLoading
        }
        confirmButtonLabel={
          confirmModalMapping[formModal.type].confirmButtonLabel
        }
      >
        {confirmModalMapping[formModal.type].body}
      </ConfirmationDialog>
      <PageBreadcrumb items={defaultBreadcrumbItems} />
      <div className="mb-9">
        <h2 className="mb-4">Users</h2>
        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex flex-wrap justify-content-between gap-3">
              <SearchBox placeholder="Search" onChange={() => {}} value="" />
              <CanAccess action="canWrite" entity="user">
                <div className="ms-xxl-auto">
                  <Button
                    variant="primary"
                    onClick={() =>
                      setFormModal({ flag: true, data: null, type: 'FORM' })
                    }
                  >
                    <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                    Invite new user
                  </Button>
                </div>
              </CanAccess>
            </div>
          </div>

          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
            <UserTable />
          </div>
        </AdvanceTableProvider>
      </div>
    </div>
  );
}

export default Users;
